import React, { useState } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { MobileFooterNav } from '../components/layout/MobileFooterNav';
import { PharmacistChat } from '../components/services/PharmacistChat';
import { PageHeroBreadcrumb } from '../components/layout/PageHeroBreadcrumb';
import { CartItem } from '../components/checkout/CartItem';
import { SEO } from '../components/shared/SEO';
import { MobileCheckoutSummary } from '../components/checkout/MobileCheckoutSummary';
import { OrderSummary } from '../components/checkout/OrderSummary';
import { CouponModal } from '../components/modals/CouponModal';
import { RewardsVouchersSection } from '../components/checkout/RewardsVouchersSection';
import { MobileHomeHeader } from '../components/MobileHomeHeader';
import { MobileInternalHeader } from '../components/layout/MobileInternalHeader';
import { LocationModal } from '../components/modals/LocationModal';
import { NowCartExperience } from '../components/now/NowCartExperience';
import { NowSubstitutionPreference } from '../components/now/NowSubstitutionPreference';
import { trackBeginCheckout } from '../lib/analytics/events';
import { toast } from 'sonner';
import { ShoppingCartIcon, CalendarIcon, TruckIcon, ShoppingBagIcon, ArrowRightIcon, ShieldCheckIcon, AwardIcon, RefreshCwIcon, HeartIcon, SnowflakeIcon } from 'lucide-react';
export function CartPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isNowMode = searchParams.get('mode') === 'now';
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<{
    name: string;
  } | null>(null);
  // Cart state
  const [cartItems, setCartItems] = useState<any[]>([{
    id: 1,
    name: 'Dyson Corrale Hair Straightener - Prussian Blue/Rich Copper',
    brand: 'Dyson',
    price: 1999,
    quantity: 1,
    image: 'https://images.unsplash.com/photo-1522338242992-e1a54906a8da?w=400&h=400&fit=crop',
    type: 'single',
    isGlobal: true
  }, {
    id: 2,
    name: 'Beamarry Argan Smooth Conditioner 380 ML',
    brand: 'Beamarry',
    price: 54.12,
    originalPrice: 85.0,
    quantity: 1,
    image: 'https://images.unsplash.com/photo-1571875257727-256c39da42af?w=400&h=400&fit=crop',
    type: 'discount',
    coldChain: true,
    nowEligible: true,
    temperatureRange: '2–8°C'
  }, {
    id: 3,
    name: 'Panadol Advance 500mg - 24 Tablets',
    brand: 'Panadol',
    price: 12.5,
    quantity: 2,
    image: 'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=400&h=400&fit=crop',
    type: 'prescription',
    requiresPrescription: true,
    nowEligible: true
  }, {
    id: 4,
    name: 'Skincare Essentials Bundle',
    brand: 'Innova Care',
    price: 245.0,
    originalPrice: 320.0,
    quantity: 1,
    image: 'https://images.unsplash.com/photo-1556228720-195a672e8a03?w=400&h=400&fit=crop',
    type: 'bundle',
    bundleItems: [{
      id: 401,
      name: 'Gentle Cleanser 200ml',
      image: 'https://images.unsplash.com/photo-1556228578-0d85b1a4d571?w=100&h=100&fit=crop'
    }, {
      id: 402,
      name: 'Hydrating Serum 30ml',
      image: 'https://images.unsplash.com/photo-1620916566398-39f1143ab7be?w=100&h=100&fit=crop'
    }, {
      id: 403,
      name: 'Moisturizing Cream 50ml',
      image: 'https://images.unsplash.com/photo-1611080626919-7cf5a9dbab5b?w=100&h=100&fit=crop'
    }]
  }, {
    id: 5,
    name: 'La Roche-Posay Anthelios Sunscreen SPF 50+',
    brand: 'La Roche-Posay',
    price: 145.0,
    quantity: 1,
    image: 'https://images.unsplash.com/photo-1598440947619-2c35fc9aa908?w=400&h=400&fit=crop',
    type: 'gift',
    giftItem: {
      id: 501,
      name: 'Travel Size Thermal Spring Water 50ml',
      image: 'https://images.unsplash.com/photo-1556229010-6c3f2c9ca5f8?w=100&h=100&fit=crop'
    }
  }, {
    id: 6,
    name: 'Muscle Recovery Combo Set',
    brand: 'Optimum Nutrition',
    price: 450.0,
    quantity: 1,
    image: 'https://images.unsplash.com/photo-1593095191071-8216adad09a7?w=400&h=400&fit=crop',
    type: 'combo',
    comboItems: [{
      id: 601,
      name: 'Whey Protein 2kg',
      image: 'https://images.unsplash.com/photo-1579722820308-d74e571900a9?w=100&h=100&fit=crop'
    }, {
      id: 602,
      name: 'BCAA Powder 300g',
      image: 'https://images.unsplash.com/photo-1593095191071-8216adad09a7?w=100&h=100&fit=crop'
    }, {
      id: 603,
      name: 'Shaker Bottle 700ml',
      image: 'https://images.unsplash.com/photo-1594498653385-d5172c532c00?w=100&h=100&fit=crop'
    }]
  }, {
    id: 7,
    name: 'Sensodyne Rapid Relief Toothpaste',
    brand: 'Sensodyne',
    price: 32.0,
    quantity: 1,
    image: 'https://images.unsplash.com/photo-1559591937-e68fb3305e5d?w=400&h=400&fit=crop',
    type: 'double'
  }]);
  // Saved for later state
  const [savedForLater, setSavedForLater] = useState([{
    id: 301,
    name: 'Hair Serum Treatment Oil',
    price: 89.99,
    image: 'https://images.unsplash.com/photo-1608248543803-ba4f8c70ae0b?w=400&h=400&fit=crop'
  }, {
    id: 302,
    name: 'Heat Protection Spray',
    price: 45.99,
    image: 'https://images.unsplash.com/photo-1629198735700-4f4791ab66b7?w=400&h=400&fit=crop'
  }]);
  // Other state
  const [orderNotes, setOrderNotes] = useState('');
  const [deliveryPreference, setDeliveryPreference] = useState<'split' | 'together'>('split');
  const [couponCode, setCouponCode] = useState('');
  const [couponModalOpen, setCouponModalOpen] = useState(false);
  // Calculate totals
  const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const discount = 451.86;
  const voucherDiscount = couponCode === 'LAKUM20' ? 20 : couponCode === 'WELCOME10' ? subtotal * 0.1 : 0;
  const tax = subtotal * 0.15;
  const total = subtotal - discount - voucherDiscount + tax;
  const chilledItems = cartItems.filter(item => item.coldChain || item.nowEligible);
  const standardItems = cartItems.filter(item => !item.coldChain && !item.nowEligible);
  const shipmentGroups = deliveryPreference === 'split' ? [{
    id: 'chilled',
    label: 'Chilled delivery within 120 minutes',
    detail: 'Temperature-protected items · 2–8°C',
    items: chilledItems,
    className: 'bg-[#16726c]'
  }, {
    id: 'standard',
    label: 'Standard delivery',
    detail: 'Items not eligible for the 120-minute service',
    items: standardItems,
    className: 'bg-[#1E2B55]'
  }].filter(group => group.items.length > 0) : [{
    id: 'together',
    label: 'Combined delivery',
    detail: 'Your full order will arrive with the slower eligible delivery window.',
    items: cartItems,
    className: 'bg-[#1E2B55]'
  }];
  // Installment options
  const installmentOptions = [{
    provider: 'Tamara',
    installments: 12,
    amount: (total / 12).toFixed(2),
    color: 'bg-[#1a1a1a]'
  }, {
    provider: 'Tabby',
    installments: 4,
    amount: (total / 4).toFixed(2),
    color: 'bg-[#3EDDB4]'
  }, {
    provider: 'MADA',
    installments: 4,
    amount: (total / 4).toFixed(2),
    color: 'bg-[#00A8E1]'
  }];
  // Handler functions
  const moveToCart = (itemId: number) => {
    const item = savedForLater.find(i => i.id === itemId);
    if (item) {
      setCartItems([...cartItems, {
        ...item,
        quantity: 1
      }]);
      setSavedForLater(savedForLater.filter(i => i.id !== itemId));
    }
  };
  const removeSavedItem = (itemId: number) => {
    setSavedForLater(savedForLater.filter(i => i.id !== itemId));
  };
  const getEstimatedDeliveryDate = () => {
    const today = new Date();
    const deliveryDate = new Date(today.setDate(today.getDate() + 2));
    return deliveryDate.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric'
    });
  };
  const applyCoupon = (code: string) => {
    const normalizedCode = code.trim().toUpperCase();
    const supportedCodes = ['LAKUM20', 'FREESHIP', 'WELCOME10'];
    if (!supportedCodes.includes(normalizedCode)) {
      toast.error('This voucher is not available for this order.');
      return false;
    }
    if (normalizedCode === 'WELCOME10' && subtotal < 3000) {
      toast.error('Add more items to unlock the 10% welcome reward.');
      return false;
    }
    setCouponCode(normalizedCode);
    toast.success(normalizedCode === 'FREESHIP' ? 'Free delivery reward saved for checkout.' : 'Voucher applied to your order.');
    return true;
  };
  // Recently viewed products
  const recentlyViewed = [{
    id: 201,
    name: 'Professional Hair Dryer 2000W',
    brand: 'SalonTech',
    price: 299.99,
    originalPrice: 399.99,
    discount: 25,
    rating: 4.9,
    reviewCount: 342,
    image: 'https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=400&h=400&fit=crop'
  }, {
    id: 202,
    name: 'Keratin Hair Mask 500ml',
    brand: 'HairCare Pro',
    price: 79.99,
    originalPrice: 99.99,
    discount: 20,
    rating: 4.7,
    reviewCount: 189,
    image: 'https://images.unsplash.com/photo-1608248543803-ba4f8c70ae0b?w=400&h=400&fit=crop'
  }, {
    id: 203,
    name: 'Curl Defining Cream',
    brand: 'CurlMaster',
    price: 56.99,
    originalPrice: 56.99,
    discount: 0,
    rating: 4.8,
    reviewCount: 234,
    image: 'https://images.unsplash.com/photo-1629198735700-4f4791ab66b7?w=400&h=400&fit=crop'
  }, {
    id: 204,
    name: 'Hair Straightening Serum',
    brand: 'SmoothSilk',
    price: 42.99,
    originalPrice: 59.99,
    discount: 28,
    rating: 4.6,
    reviewCount: 167,
    image: 'https://images.unsplash.com/photo-1616394584738-fc6e612e71b9?w=400&h=400&fit=crop'
  }, {
    id: 205,
    name: 'Volume Boosting Mousse',
    brand: 'VolumePro',
    price: 47.99,
    originalPrice: 64.99,
    discount: 26,
    rating: 4.5,
    reviewCount: 198,
    image: 'https://images.unsplash.com/photo-1522338242992-e1a54906a8da?w=400&h=400&fit=crop'
  }, {
    id: 206,
    name: 'Anti-Frizz Hair Oil',
    brand: 'FrizzControl',
    price: 52.99,
    originalPrice: 72.99,
    discount: 27,
    rating: 4.8,
    reviewCount: 223,
    image: 'https://images.unsplash.com/photo-1571875257727-256c39da42af?w=400&h=400&fit=crop'
  }];
  const handleProceedToCheckout = () => {
    trackBeginCheckout(cartItems.map(item => ({
      item_id: item.id.toString(),
      item_name: item.name,
      price: item.price,
      quantity: item.quantity
    })));
    navigate('/checkout');
  };
  if (isNowMode) return <NowCartExperience data-id="element-6394" data-mp="2465d1u0yw55n" />;
  return <HelmetProvider data-id="element-6395" data-mp="2453r2uiexy95">
      <SEO title="Cart" description="Review your cart at Innova Pharmacy — check quantities, prices, and promotions before checkout. Online pharmacy with fast delivery across Saudi Arabia." canonical="/en/cart/" robots="noindex,noimageindex,noarchive,follow" hreflangAr="/cart/" hreflangEn="/en/cart/" hreflangDefault="/cart/" data-id="element-6396" data-mp="243j5vvhznrny" />
      <div className="flex flex-col min-h-screen bg-gray-50" data-id="element-6397" data-mp="2mtqwje4i4pn9">
        {/* Mobile Header - Only on mobile */}
        <div className="md:hidden fixed top-0 left-0 right-0 z-50" data-id="element-6398" data-mp="2rskbzc3cmqq2">
          <MobileInternalHeader title="Shopping Cart" data-id="element-6399" data-mp="227aus40z0119" />
        </div>
        {/* Desktop Header - Only on desktop */}
        <div className="hidden md:block" data-id="element-6400" data-mp="1x5lh0gmd599h">
          <Header locationModalOpen={locationModalOpen} setLocationModalOpen={setLocationModalOpen} selectedLocation={selectedLocation} setSelectedLocation={setSelectedLocation} data-id="element-6401" data-mp="2bwubp454hkth" />
        </div>
        <main className="flex-grow pt-14 md:pt-[var(--header-height-desktop)] pb-40 md:pb-0" data-id="element-6402" data-mp="2kzdczjt9fpdm">
          <h1 className="sr-only" data-id="element-6403" data-mp="2fxxn9eg50uow">Shopping Cart</h1>
          {/* Page Hero Breadcrumb */}
          <PageHeroBreadcrumb breadcrumbs={[{
          label: 'Home',
          href: '/'
        }, {
          label: 'Shopping Cart'
        }]} title="Shopping Cart" description="Review your items and proceed to checkout" data-id="element-6404" data-mp="2uuota6kbh4nh" />
          <div className="container mx-auto px-4 py-4 md:py-6 lg:py-8" data-id="element-6405" data-mp="37560rzopd2ae">
            {cartItems.length > 0 ? <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6" data-id="element-6406" data-mp="3c525k1584xr0">
                {/* Cart Items - Main Area */}
                <div className="lg:col-span-2 space-y-4 md:space-y-6" data-id="element-6407" data-mp="2xfczpsm6s5ke">
                  {/* Free Shipping Progress Bar */}
                  <div className="bg-white rounded-lg shadow-sm p-4 md:p-6 border border-blue-100" data-id="element-6408" data-mp="32cmmt1m3s552">
                    <div className="flex items-center justify-between mb-2" data-id="element-6409" data-mp="2ohklkrs7b3yl">
                      <div className="flex items-center gap-2" data-id="element-6410" data-mp="2si4hjs11g1j5">
                        <TruckIcon className="w-5 h-5 text-[#1E2B55]" data-id="element-6411" data-mp="2ehtr2q3n4oyi" />
                        <span className="font-bold text-gray-900" data-id="element-6412" data-mp="2jfv7f39oma6a">
                          Free Shipping
                        </span>
                      </div>
                      <span className="text-sm font-medium text-[#1E2B55]" data-id="element-6413" data-mp="cqgjftaag2yr">
                        {total >= 200 ? 'Unlocked!' : `Add ${(200 - total).toFixed(2)} SAR more`}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5 mb-1" data-id="element-6414" data-mp="28u3g2v21g7su">
                      <div className={`h-2.5 rounded-full ${total >= 200 ? 'bg-green-500' : 'bg-[#1E2B55]'}`} style={{
                    width: `${Math.min(total / 200 * 100, 100)}%`
                  }} data-id="element-6415" data-mp="24q5hclgmkz0b"></div>
                    </div>
                    <p className="text-xs text-gray-500 mt-2" data-id="element-6416" data-mp="1q0r8degyojlg">
                      Free shipping on orders over 200 SAR
                    </p>
                  </div>

                  <section className="rounded-xl border border-[#b8e2df] bg-[#f2fbfa] p-4" aria-labelledby="delivery-choice-heading" data-id="element-6417" data-mp="1h2f8elu7g9gc">
                    <div className="flex items-start gap-3" data-id="element-6418" data-mp="1c1rik3hppynp"><span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#16726c] text-white" data-id="element-6419" data-mp="173pzhikadzc8"><SnowflakeIcon className="h-5 w-5" data-id="element-6420" data-mp="3ir1uml4ylxep" /></span><div data-id="element-6421" data-mp="3iq0azs9vo2ld"><h2 id="delivery-choice-heading" className="text-sm font-bold text-[#173c3a]" data-id="element-6422" data-mp="3iq9z1abd3zof">Choose how your order arrives</h2><p className="mt-1 text-xs leading-5 text-[#416b67]" data-id="element-6423" data-mp="3no1s47nqtru7">Some items are eligible for our temperature-protected 120-minute delivery.</p></div></div>
                    <div className="mt-4 grid gap-2 sm:grid-cols-2" data-id="element-6424" data-mp="lgf8zvhx6nd">
                      <button type="button" onClick={() => setDeliveryPreference('split')} className={`rounded-lg border p-3 text-left transition-[background-color,border-color] duration-150 ${deliveryPreference === 'split' ? 'border-[#16726c] bg-white ring-1 ring-[#16726c]' : 'border-[#cfe4e2] bg-transparent'}`} data-id="element-6425" data-mp="300k66sr5tnz5">
                        <span className="block text-sm font-bold text-gray-900" data-id="element-6426" data-mp="3ds8e0judlqab">Deliver separately</span><span className="mt-1 block text-xs text-gray-600" data-id="element-6427" data-mp="2h9ss5dw899ef">Chilled items arrive within 120 minutes where eligible.</span>
                      </button>
                      <button type="button" onClick={() => setDeliveryPreference('together')} className={`rounded-lg border p-3 text-left transition-[background-color,border-color] duration-150 ${deliveryPreference === 'together' ? 'border-[#16726c] bg-white ring-1 ring-[#16726c]' : 'border-[#cfe4e2] bg-transparent'}`} data-id="element-6428" data-mp="2l9l3fyd09tnh">
                        <span className="block text-sm font-bold text-gray-900" data-id="element-6429" data-mp="3td1qqq1uyilx">Deliver together</span><span className="mt-1 block text-xs text-gray-600" data-id="element-6430" data-mp="3tf4qi1stbq3u">Your full order follows the slower eligible window.</span>
                      </button>
                    </div>
                  </section>

                  <NowSubstitutionPreference data-id="element-6431" data-mp="30qpdtlx5ljvo" />

                  <div className="space-y-6" data-id="element-6432" data-mp="3ep6uizon5w43">
                    {shipmentGroups.map((group, groupIndex) => <section key={group.id} className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm" aria-labelledby={`shipment-${group.id}`} data-id="element-6433" data-mp="3akz5t2i6og5v" data-mpi={groupIndex}>
                        <div className={`${group.className} flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-white`} data-id="element-6434" data-mp="px314eraxwpx">
                          <div data-id="element-6435" data-mp="xe9d32wbt008"><h2 id={`shipment-${group.id}`} className="text-sm font-bold" data-id="element-6436" data-mp="xej0cipzkt28">{shipmentGroups.length > 1 ? `Delivery ${groupIndex + 1} of ${shipmentGroups.length}` : 'Your delivery'}</h2><p className="mt-0.5 text-xs text-white/85" data-id="element-6437" data-mp="nipqx87kdfoo">{group.label}</p></div>
                          <span className="inline-flex items-center gap-1 rounded border border-white/35 px-2 py-1 text-[10px] font-bold" data-id="element-6438" data-mp="sfy38s616pi0"><TruckIcon className="h-3 w-3" data-id="element-6439" data-mp="1g7myaga8ls2n" />{group.id === 'chilled' ? 'Protected' : group.id === 'together' ? 'Combined' : 'Standard'}</span>
                        </div>
                        <div className="p-3 md:p-4 lg:p-6" data-id="element-6440" data-mp="16hafotlzg8z5"><p className="mb-4 text-xs font-medium text-gray-500" data-id="element-6441" data-mp="16g8rrojvbs00">{group.detail}</p><div className="space-y-3 md:space-y-4" data-id="element-6442" data-mp="1bek2e1hzm6ja">{group.items.map(item => <CartItem key={item.id} item={item} onQuantityChange={(id, quantity) => setCartItems(cartItems.map(cartItem => cartItem.id === id ? {
                        ...cartItem,
                        quantity
                      } : cartItem))} onRemove={id => setCartItems(cartItems.filter(cartItem => cartItem.id !== id))} onSaveForLater={id => {
                        const itemToSave = cartItems.find(cartItem => cartItem.id === id);
                        if (itemToSave) {
                          setSavedForLater([...savedForLater, itemToSave]);
                          setCartItems(cartItems.filter(cartItem => cartItem.id !== id));
                        }
                      }} data-id="element-6443" data-mp="1z5g1zksoq613" />)}</div></div>
                      </section>)}
                  </div>

                  <div className="mt-4 lg:hidden" data-id="element-6444" data-mp="mt12w96idhzr">
                    <RewardsVouchersSection voucherCode={couponCode} orderTotal={total} onVoucherCodeChange={setCouponCode} onApplyVoucher={applyCoupon} onViewAllVouchers={() => setCouponModalOpen(true)} data-id="element-6445" data-mp="mrpz49d2ca8n" />
                  </div>
                </div>

                {/* Right Column - Desktop Only */}
                <div className="hidden lg:block lg:col-span-1" data-id="element-6446" data-mp="2tiywc0ed8l4f">
                  <div className="sticky top-24 space-y-6" data-id="element-6447" data-mp="2thnqlv1d13ao">
                    <RewardsVouchersSection voucherCode={couponCode} orderTotal={total} onVoucherCodeChange={setCouponCode} onApplyVoucher={applyCoupon} onViewAllVouchers={() => setCouponModalOpen(true)} data-id="element-6448" data-mp="2jftl1925ofss" />
                    <OrderSummary subtotal={subtotal} discount={discount + voucherDiscount} tax={tax} total={total} itemCount={cartItems.reduce((acc, item) => acc + item.quantity, 0)} onOpenCoupons={() => setCouponModalOpen(true)} data-id="element-6449" data-mp="2y597icunlz2o" />
                  </div>
                </div>
              </div> : <div className="bg-white rounded-lg shadow-sm p-8 md:p-12 text-center mb-8" data-id="element-6450" data-mp="1mohuffrwj1n0">
                <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-6451" data-mp="3qste933ec77r">
                  <ShoppingBagIcon className="w-10 h-10 text-gray-400" data-id="element-6452" data-mp="3utwrdhu27p5a" />
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-2" data-id="element-6453" data-mp="3odgui3shvxd">
                  Your cart is empty
                </h2>
                <p className="text-gray-500 mb-6 max-w-md mx-auto" data-id="element-6454" data-mp="8flr1gcy1lbl">
                  Looks like you haven't added anything to your cart yet.
                  Explore our top categories and find what you need.
                </p>
                <button onClick={() => navigate('/')} className="inline-flex items-center justify-center gap-2 bg-[#1E2B55] text-white font-bold py-3 px-8 rounded-lg hover:bg-[#162041] transition-colors" data-id="element-6455" data-mp="if45xidzygws">
                  Continue Shopping
                  <ArrowRightIcon className="w-5 h-5" data-id="element-6456" data-mp="2o82mxqkocz1z" />
                </button>
              </div>}

            {/* Recently Viewed (Empty State) */}
            {cartItems.length === 0 && <div className="mt-6 md:mt-8" data-id="element-6457" data-mp="2lnczhxqric5a">
                <div className="flex items-center justify-between mb-4 md:mb-6" data-id="element-6458" data-mp="2gujwpfeutdvu">
                  <h2 className="text-lg md:text-xl lg:text-2xl font-bold" data-id="element-6459" data-mp="2bv77io0ifuq3">
                    Recently Viewed
                  </h2>
                </div>
                <div className="flex overflow-x-auto md:grid md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4 pb-4 md:pb-0 snap-x snap-mandatory hide-scrollbar -mx-4 px-4 md:mx-0 md:px-0" data-id="element-6460" data-mp="26ygpvw924r2k">
                  {recentlyViewed.map(product => <div key={product.id} className="flex-none w-[160px] md:w-auto bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow snap-start" data-id="element-6461" data-mp="2uloxwlxvee8a">
                      <div className="relative aspect-square bg-gray-50 overflow-hidden" data-id="element-6462" data-mp="1j4yn0pii4y8c">
                        <img src={product.image} alt={product.name} className="w-full h-full object-cover" data-id="element-6463" data-mp="1f46fc0pa31e6" />
                        {product.discount > 0 && <div className="absolute top-2 left-2 bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full" data-id="element-6464" data-mp="16nfiglo9tyxo">
                            -{product.discount}%
                          </div>}
                      </div>
                      <div className="p-2 md:p-3" data-id="element-6465" data-mp="12mvkxgzsctb0">
                        <p className="text-xs text-gray-500 mb-1 uppercase tracking-wide" data-id="element-6466" data-mp="12of1l8pct6db">
                          {product.brand}
                        </p>
                        <h3 className="text-xs md:text-sm font-medium text-gray-900 line-clamp-2 mb-2 min-h-[32px] md:min-h-[40px]" data-id="element-6467" data-mp="swj1xt7md86z">
                          {product.name}
                        </h3>
                        <div className="flex flex-col mb-2 md:mb-3" data-id="element-6468" data-mp="ized1lme2ymh">
                          {product.originalPrice && <span className="text-xs text-gray-400 line-through" data-id="element-6469" data-mp="bdmraxj1u6b">
                              {product.originalPrice} SAR
                            </span>}
                          <span className="text-sm md:text-base font-bold text-red-600" data-id="element-6470" data-mp="9c2166qu1wjq">
                            {product.price} SAR
                          </span>
                        </div>
                        <button className="w-full bg-[#1E2B55] hover:bg-[#fdc300] hover:text-[#1E2B55] text-white font-medium py-1.5 md:py-2 px-2 md:px-4 rounded-lg transition-colors text-xs md:text-sm" data-id="element-6471" data-mp="j85co37kr7jc">
                          Add to Cart
                        </button>
                      </div>
                    </div>)}
                </div>
              </div>}
          </div>
        </main>
        <div className="hidden md:block" data-id="element-6472" data-mp="11xtwp19w13jx">
          <Footer data-id="element-6473" data-mp="x1mm2v6jr3vn" />
        </div>

        {cartItems.length > 0 && <MobileCheckoutSummary subtotal={subtotal} discount={discount + voucherDiscount} tax={tax} total={total} onCheckout={handleProceedToCheckout} data-id="element-6474" data-mp="1h2xyw30nidbm" />}

        <MobileFooterNav data-id="element-6475" data-mp="1puzw7sffbdho" />
        <div className="hidden md:block" data-id="element-6476" data-mp="1pvt5b01uuf2z">
          <PharmacistChat data-id="element-6477" data-mp="1uucqgy8mjmua" />
        </div>
        <LocationModal isOpen={locationModalOpen} onClose={() => setLocationModalOpen(false)} onSelectLocation={location => {
        setSelectedLocation(location);
        setLocationModalOpen(false);
      }} data-id="element-6478" data-mp="1ut1eib50z8s3" />
        <CouponModal isOpen={couponModalOpen} onClose={() => setCouponModalOpen(false)} onApplyCoupon={code => {
        applyCoupon(code);
        setCouponModalOpen(false);
      }} data-id="element-6479" data-mp="1751mz8zw3ecg" />
      </div>
    </HelmetProvider>;
}